import React, { useState, useEffect } from 'react';
import Sidebar from './components/Navigation/Sidebar';
import CompanySelector from './components/CompanySelector';
import { useCompany } from './contexts/CompanyContext';
import EmployeesModule from './components/Employees/EmployeesModule';
import PrestacionesModule from './components/Prestaciones/PrestacionesModule';
import OrganigramaModule from './components/Organigrama/OrganigramaModule';
import AsistenciaModule from './components/Asistencia/AsistenciaModule';
import VacacionesModule from './components/Vacaciones/VacacionesModule';
import VacationsModule from './components/Vacations/VacationsModule';
import ShiftManagementModule from './components/Attendance/ShiftManagement/ShiftManagementModule';
import UsuariosModule from './components/Usuarios/UsuariosModule';
import SolicitudesModule from './components/Solicitudes/SolicitudesModule';
import CompaniesModule from './components/Companies/CompaniesModule';
import PayrollProcessingModule from './components/Payroll/PayrollProcessing/PayrollProcessingModule';
import PrestamosModule from './components/Payroll/PrestamosModule';
import PayrollComparison from './components/Payroll/PayrollComparison';
import PayrollStatistics from './components/Payroll/PayrollStatistics';
import IngresosVariablesModule from './components/Payroll/IngresosVariablesModule';
import SocialSecurityModule from './components/Payroll/SocialSecurityModule';
import OtherDeductionsModule from './components/Payroll/OtherDeductionsModule/OtherDeductionsModule';
import ISRModule from './components/Payroll/ISRModule/ISRModule';
import TaxBenefitsModule from './components/Payroll/TaxBenefitsModule/TaxBenefitsModule';
import RelatedPersonsModule from './components/RelatedPersons/RelatedPersonsModule';
import ContractsModule from './components/HR/Contracts/ContractsModule';
import PersonalActionsModule from './components/HR/PersonalActions/PersonalActionsModule';
import DocumentsModule from './components/HR/Documents/DocumentsModule';
import HistoryModule from './components/HR/History/HistoryModule';
import Dashboard from './components/Dashboard/Dashboard';
import CafeteriaModule from './components/Cafeteria/CafeteriaModule';
import AprobacionesModule from './components/Aprobaciones/AprobacionesModule';
import CalendarModule from './components/HR/Calendar/CalendarModule';
import AbsencesModule from './components/HR/Absences/AbsencesModule';
import PayrollConfigModule from './components/Payroll/PayrollConfig/PayrollConfigModule';
import ProvisionsModule from './components/Payroll/Provisions/ProvisionsModule';
import PayrollReportsModule from './components/Payroll/Reports/PayrollReportsModule';
import TSSModule from './components/Payroll/TSSModule/TSSModule';
import IR13Module from './components/Payroll/IR13Module/IR13Module';
import BonificacionesModule from './components/Payroll/BonificacionesModule/BonificacionesModule';
import CreditosISRModule from './components/Payroll/CreditosISR/CreditosISRModule';
import RegaliaPascualModule from './components/Payroll/RegaliaPascual/RegaliaPascualModule';
import AuditModule from './components/Audit/AuditModule';
import RolesModule from './components/Admin/Security/RolesModule';
import PermissionsModule from './components/Admin/Security/PermissionsModule';
import { getConteo } from './api/aprobaciones';
import { usePermissionsContext } from './contexts/PermissionsContext';


export default function SevenFamilyHR({ currentUser, onLogout }) {
  const [currentModule, setCurrentModule] = useState('dashboard');
  const { loading: companyLoading, currentCompany, switchPhase } = useCompany();
  const [pendientesCount, setPendientesCount] = useState(0);
  const [deepLink, setDeepLink] = useState(null); // { id, employeeId?, periodo?, quincena? }

  const navigateTo = (module, linkData = {}) => {
    setDeepLink(linkData);
    setCurrentModule(module);
  };
  const clearDeepLink = () => setDeepLink(null);

  const { can } = usePermissionsContext();

  // Polling del conteo de aprobaciones pendientes (cada 60s)
  const isAdminUser = can('REQUEST_APPROVE');

  useEffect(() => {
    if (!isAdminUser) return;
    const fetchConteo = async () => {
      try {
        const data = await getConteo();
        setPendientesCount(data.total || 0);
      } catch {
        // Silently fail — no interrumpir la sesión por un error de conteo
      }
    };
    fetchConteo();
    const interval = setInterval(fetchConteo, 60000);
    return () => clearInterval(interval);
  }, [isAdminUser]);

  if (companyLoading) {
    return (
      <div className="min-h-screen bg-blue-600 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-lg font-semibold">Cargando información de la compañía...</p>
        </div>
      </div>
    );
  }

  if (!currentCompany) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Sin acceso a compañías
          </h2>
          <p className="text-gray-600 mb-6">
            Tu cuenta no tiene acceso a ninguna compañía. Contacta al administrador del sistema.
          </p>
          <button
            onClick={onLogout}
            className="px-6 py-3 bg-gray-600 text-white rounded-xl font-semibold hover:bg-gray-700"
          >
            Cerrar Sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar
        currentModule={currentModule}
        setCurrentModule={setCurrentModule}
        onLogout={onLogout}
        currentUser={currentUser}
        currentCompany={currentCompany}
        pendientesCount={pendientesCount}
      />

      <div className="lg:ml-64 transition-all duration-300">
        <div className="bg-white border-b shadow-sm sticky top-0 z-30">
          <div className="px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 pl-10 lg:pl-0 min-w-0">
                <h2 className="text-lg lg:text-2xl font-bold text-gray-800 truncate">
                  {getCurrentModuleName(currentModule)}
                </h2>
                <p className="hidden lg:block text-sm text-gray-500 mt-1">
                  {getCurrentModuleDescription(currentModule)}
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <CompanySelector />
                
              </div>
            </div>
          </div>
        </div>

        {/* Overlay de transición al cambiar de compañía */}
        {switchPhase !== 'idle' && (
          <div
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white pointer-events-none"
            style={{
              opacity:    switchPhase === 'fading-out' ? 0 : switchPhase === 'switching' ? 1 : 0,
              transition: switchPhase === 'fading-out'
                ? 'opacity 300ms ease-in'
                : switchPhase === 'fading-in'
                ? 'opacity 350ms ease-out'
                : 'none',
            }}
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-600 flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-1.5 h-1.5 rounded-full bg-purple-600 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <p className="text-sm font-medium text-gray-500">
                Cambiando compañía{currentCompany ? ` a ${currentCompany.nombre}` : ''}…
              </p>
            </div>
          </div>
        )}

        <main
          key={currentCompany?.id}
          className="p-4 lg:p-8"
          style={{
            opacity:    switchPhase === 'fading-out' ? 1 : switchPhase === 'switching' ? 0 : 1,
            transition: switchPhase === 'fading-in' ? 'opacity 350ms ease-out' : 'none',
          }}
        >
          {/* ⭐ DASHBOARD */}
          {currentModule === 'dashboard' && <Dashboard currentUser={currentUser} />}

          {/* ⭐ SUBMÓDULOS DE GESTIÓN DE PERSONAL */}
          {currentModule === 'employees' && <EmployeesModule currentUser={currentUser} />}
          {currentModule === 'related-persons' && <RelatedPersonsModule />}
          {currentModule === 'contracts' && <ContractsModule />}
          {currentModule === 'personnel-actions' && <PersonalActionsModule deepLinkId={deepLink?.id} onDeepLinkConsumed={clearDeepLink} />}
          {currentModule === 'documents' && <DocumentsModule />}
          {currentModule === 'history' && <HistoryModule />}

          {currentModule === 'org-chart'       && <OrganigramaModule view="chart" />}
          {currentModule === 'org-units'       && <OrganigramaModule view="units" />}
          {currentModule === 'org-departments' && <OrganigramaModule view="departments" />}
          {currentModule === 'org-positions'   && <OrganigramaModule view="positions" />}
          {currentModule === 'shift-management' && <ShiftManagementModule />}

          {/* ⭐ SUBMÓDULOS DE NÓMINA */}
          {currentModule === 'payroll-main' && <PayrollProcessingModule />}
          {currentModule === 'variable-incomes' && <IngresosVariablesModule deepLinkEmployeeId={deepLink?.employeeId} deepLinkPeriodo={deepLink?.periodo} deepLinkQuincena={deepLink?.quincena} onDeepLinkConsumed={clearDeepLink} />}
          {currentModule === 'social-security' && <SocialSecurityModule />}
          {currentModule === 'tss-planillas' && <TSSModule currentCompany={currentCompany} />}
          {currentModule === 'isr' && <ISRModule />}
          {currentModule === 'ir13' && <IR13Module />}
          {currentModule === 'creditos-isr' && <CreditosISRModule currentCompany={currentCompany} />}
          {currentModule === 'bonificaciones' && <BonificacionesModule />}
          {currentModule === 'regalia-pascual' && <RegaliaPascualModule />}
          {currentModule === 'other-deductions' && <OtherDeductionsModule />}
          {currentModule === 'tax-benefits' && <TaxBenefitsModule />}
          {currentModule === 'prestamos' && <PrestamosModule />}
          {currentModule === 'compare-payrolls' && <PayrollComparison />}
          {currentModule === 'payroll-statistics' && <PayrollStatistics />}
          {currentModule === 'payroll-config' && <PayrollConfigModule />}
          {currentModule === 'provisions' && <ProvisionsModule />}
          {currentModule === 'payroll-reports' && <PayrollReportsModule />}

          {currentModule === 'prestaciones' && <PrestacionesModule />}
          {currentModule === 'vacaciones' && <VacacionesModule />}
          {currentModule === 'vacations' && <VacationsModule currentUser={currentUser} />}
          {currentModule === 'calendar' && <CalendarModule currentUser={currentUser} />}
          {currentModule === 'absences' && <AbsencesModule currentUser={currentUser} deepLinkId={deepLink?.id} onDeepLinkConsumed={clearDeepLink} />}
          {currentModule === 'aprobaciones' && <AprobacionesModule currentUser={currentUser} onNavigateTo={navigateTo} />}
          {currentModule === 'cafeteria' && <CafeteriaModule currentUser={currentUser} />}
          {currentModule === 'solicitudes' && <SolicitudesModule currentCompany={currentCompany} currentUser={currentUser} deepLinkId={deepLink?.id} onDeepLinkConsumed={clearDeepLink} />}
          {currentModule === 'usuarios' && <UsuariosModule />}
          {currentModule === 'roles' && <RolesModule />}
          {currentModule === 'permisos' && <PermissionsModule />}
          {currentModule === 'companies' && <CompaniesModule />}
          {currentModule === 'audit' && <AuditModule />}
        </main>
      </div>
    </div>
  );
}

function getCurrentModuleName(moduleId) {
  const names = {
    dashboard: 'Dashboard',
    employees: 'Empleados',
    'related-persons': 'Personas Relacionadas',
    contracts: 'Contratos',
    'personnel-actions': 'Acciones de Personal',
    documents: 'Documentos',
    history: 'Historial de Personal',
    'org-structure':    'Estructura Organizacional',
    'org-chart':        'Organigrama',
    'org-units':        'Unidades Organizativas',
    'org-departments':  'Departamentos',
    'org-positions':    'Cargos',
    'attendance-control': 'Control de Asistencia',
    'shift-management': 'Turnos y Horarios',
    'payroll-main': 'Procesar Nómina',
    'variable-incomes': 'Ingresos Variables',
    'social-security': 'Seguridad Social',
    'tss-planillas': 'Planillas TSS',
    'isr': 'ISR',
    'ir13': 'Conciliación de IR-13',
    'creditos-isr': 'Créditos ISR',
    'other-deductions': 'Otras Deducciones',
    'tax-benefits': 'Beneficios Fiscales',
    prestamos: 'Préstamos',
    'compare-payrolls': 'Comparar Nóminas',
    'payroll-statistics': 'Estadísticas de Nómina',
    'payroll-config': 'Configuración de Nómina',
    'provisions': 'Provisiones Laborales',
    'payroll-reports': 'Reportes de Nómina',
    prestaciones: 'Prestaciones',
    vacaciones: 'Vacaciones',
    calendar: 'Calendario Laboral',
    absences: 'Gestión de Ausencias',
    cafeteria: 'Cafetería',
    aprobaciones: 'Aprobaciones',
    solicitudes: 'Solicitudes',
    usuarios: 'Usuarios',
    roles: 'Roles',
    permisos: 'Permisos',
    companies: 'Gestión de Compañías',
    audit: 'Auditoría'
  };
  return names[moduleId] || 'Dashboard';
}

function getCurrentModuleDescription(moduleId) {
  const descriptions = {
    dashboard: 'Vista general del sistema con estadísticas principales',
    employees: 'Gestiona la información del personal de tu empresa',
    'related-persons': 'Dependientes, familiares, contactos de emergencia y beneficiarios',
    contracts: 'Administra contratos laborales y renovaciones',
    'personnel-actions': 'Gestiona promociones, cambios de puesto, sanciones y reconocimientos',
    documents: 'Almacena y administra documentos del personal',
    history: 'Consulta el historial completo de cambios y acciones del personal',
    'org-structure':    'Visualiza y gestiona la estructura organizacional de la empresa',
    'org-chart':        'Vista jerárquica visual de la estructura de la empresa',
    'org-units':        'Gestiona sucursales, divisiones, sedes y regiones de la empresa',
    'org-departments':  'Gestiona los departamentos de la empresa',
    'org-positions':    'Gestiona los cargos y perfiles de puesto',
    'attendance-control': 'Control de asistencia, turnos, horarios y vacaciones',
    'shift-management': 'Crea, edita y asigna turnos y horarios a empleados',
    'payroll-main': 'Procesa nóminas quincenales y mensuales',
    'variable-incomes': 'Gestiona comisiones, vacaciones, incentivos, bonificaciones y otros ingresos',
    'social-security': 'Configura TSS (AFP, SFS, SRL, INFOTEP) y cápita por empleado',
    'isr': 'Simulador de Impuesto Sobre la Renta - Motor de cálculo para nómina',
    'ir13': 'Gestiona la declaración anual de retenciones ISR IR-13 (DGII)',
    'creditos-isr': 'Saldos a favor ISR generados en IR-13 o de forma manual, aplicados FIFO en nómina',
    'other-deductions': 'Gestiona seguros, embargos, cuotas sindicales y otras deducciones',
    'tax-benefits': 'Gastos educativos, intereses hipotecarios y dependientes adicionales (DGII)',
    prestamos: 'Gestiona préstamos a empleados con descuento automático en nómina',
    'compare-payrolls': 'Compara y analiza diferencias entre períodos de nómina',
    'payroll-statistics': 'Análisis de KPIs, tendencias y estadísticas de nómina',
    'payroll-config': 'Parámetros globales, tasas TSS, conceptos y catálogos de nómina',
    'provisions': 'Acumulación mensual de regalía, vacaciones, cesantía y preaviso por empleado',
    'payroll-reports': 'Genera, visualiza y exporta reportes de nóminas cerradas en PDF, Excel y CSV',
    prestaciones: 'Gestiona los beneficios laborales',
    vacaciones: 'Administra las solicitudes de vacaciones',
    calendar: 'Define días laborables, feriados y eventos especiales de la empresa',
    absences: 'Administra vacaciones, permisos, licencias médicas y otras ausencias',
    cafeteria: 'Gestiona menús diarios, selecciones y reportes de cafetería',
    aprobaciones: 'Revisa y gestiona todas las solicitudes pendientes de aprobación',
    solicitudes: 'Centro de solicitudes del personal — préstamos, vacaciones, certificaciones, IT y más',
    usuarios: 'Administra los usuarios del sistema',
    roles: 'Gestiona los roles del sistema y sus permisos asignados',
    permisos: 'Catálogo completo de permisos disponibles por módulo',
    companies: 'Administra las compañías del sistema multi-tenant',
    audit: 'Registro inmutable de todas las acciones del sistema'
  };
  return descriptions[moduleId] || 'Panel de control';
}
